import { useState } from 'react';
import { Form, Container, Row, Col, Image } from 'react-bootstrap';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { saveShippingAddress, savePaymentMethod, clearCartItems, openCart } from '../slices/cartSlice';
import { useGetCountriesQuery } from '../slices/productsApiSlice';
import { useCreateOrderMutation, useCreateGuestOrderMutation } from '../slices/ordersApiSlice';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';
import Loader from '../components/Loader';
import payMethodsEn from '../assets/accepted-payment-methods-left-clean.png';
import payMethodsAr from '../assets/accepted-payment-methods-right-clean.png';

// Unified one-page checkout: order summary + contact + delivery + payment.
const sectionTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '17px',
  fontWeight: 600,
  margin: '26px 0 14px',
  color: '#2c2c2c',
};
const stepBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  minWidth: '26px',
  borderRadius: '50%',
  background: '#7C9D64',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 700,
};
const errStyle = { color: '#c0392b', fontSize: 12.5, marginTop: 4 };

const WHATSAPP_NUMBER = '966548036906';

const isKSAName = (c) => c === 'Saudi Arabia' || c === 'المملكة العربية السعودية';

const CheckoutScreen = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language !== 'en';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();

  const cart = useSelector((state) => state.cart);
  const { shippingAddress, cartItems } = cart;
  const { userInfo } = useSelector((state) => state.auth);
  const acct = (userInfo && userInfo.user) || {};

  const [address, setAddress] = useState(shippingAddress.address || '');
  const [city, setCity] = useState(shippingAddress.city || '');
  const [country, setCountry] = useState(shippingAddress.country || '');

  const initialType = shippingAddress.type || 'default';
  const [isGift, setIsGift] = useState(initialType === 'gift' || initialType === 'someone');

  const [recipientName, setRecipientName] = useState(shippingAddress.name || '');
  const [recipientPhone, setRecipientPhone] = useState(shippingAddress.phone || '');
  const [giftMessage, setGiftMessage] = useState(shippingAddress.message || '');

  // Prefill from saved address, then from the logged-in account.
  const [fullName, setFullName] = useState(shippingAddress.name || acct.name || '');
  const [phone, setPhone] = useState(shippingAddress.phone || acct.phone || '');
  const [email, setEmail] = useState(shippingAddress.email || acct.email || '');

  const [errors, setErrors] = useState({});
  const [placing, setPlacing] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false); // mobile: collapsed by default
  const [phoneCountry] = useState('sa'); // Saudi default; users can change via the flag dropdown

  const { data: countries, isLoading, error } = useGetCountriesQuery();
  const [createOrder] = useCreateOrderMutation();
  const [createGuestOrder] = useCreateGuestOrderMutation();

  const ksa = isKSAName(country);

  const syncShipping = (next = {}) => {
    dispatch(
      saveShippingAddress({
        address,
        city,
        country,
        email,
        type: isGift ? 'gift' : 'default',
        name: isGift ? recipientName : fullName,
        phone: isGift ? recipientPhone : phone,
        message: isGift ? giftMessage : '',
        ...next,
      })
    );
  };

  const handleCountryChange = (e) => {
    const selected = e.target.value;
    setCountry(selected);
    setCity('');
    syncShipping({ country: selected, city: '' });
  };

  const digitsOf = (v) => (v || '').replace(/\D/g, '');

  const validate = () => {
    const e = {};
    if (!fullName.trim()) e.name = isAr ? 'الاسم مطلوب' : 'Name is required';
    if (digitsOf(phone).length < 8) e.phone = isAr ? 'أدخل رقم جوال صحيح' : 'Enter a valid phone number';
    if (!country) e.country = isAr ? 'يرجى اختيار الدولة' : 'Please select a country';
    if (!city) e.city = isAr ? 'المدينة مطلوبة' : 'City is required';
    if (!address.trim()) e.address = isAr ? 'العنوان مطلوب' : 'Address is required';

    const em = email.trim();
    if (!isKSAName(country) && !em) {
      e.email = isAr ? 'البريد الإلكتروني مطلوب للطلبات الدولية' : 'Email is required for international orders';
    } else if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      e.email = isAr ? 'بريد إلكتروني غير صحيح' : 'Invalid email address';
    }

    if (isGift) {
      if (!recipientName.trim()) e.recipientName = isAr ? 'اسم المستلم مطلوب' : 'Recipient name is required';
      if (digitsOf(recipientPhone).length < 8) e.recipientPhone = isAr ? 'أدخل رقم جوال صحيح' : 'Enter a valid phone number';
    }
    return e;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) return;

    const eobj = validate();
    setErrors(eobj);
    if (Object.keys(eobj).length) {
      const first = document.querySelector(`[data-field="${Object.keys(eobj)[0]}"]`);
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setPlacing(true);
    try {
      syncShipping();
      dispatch(savePaymentMethod('electronic'));

      const freshCart = store.getState().cart;
      const totalWeight = cartItems.reduce((acc, item) => acc + parseFloat(item.weight) * item.qty, 0);
      const orderName = isGift ? recipientName : fullName;
      const orderPhone = isGift ? recipientPhone : phone;

      const orderPayload = {
        orderItems: cartItems,
        address,
        country,
        city,
        email,
        paymentMethod: 'electronic',
        itemsPrice: freshCart.itemsPrice,
        shippingPrice: freshCart.shippingPrice,
        totalPrice: freshCart.totalPrice,
        weight: totalWeight,
        discount: freshCart.discount,
        type: isGift ? 'gift' : 'default',
        name: orderName,
        phone: orderPhone,
        message: isGift ? giftMessage : '',
      };

      const res = userInfo
        ? await createOrder(orderPayload).unwrap()
        : await createGuestOrder(orderPayload).unwrap();

      dispatch(clearCartItems());
      navigate(`/pay/${res.order.id}`);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
      setPlacing(false);
    }
  };

  if (isLoading) return <Loader />;

  if (cartItems.length === 0) {
    return (
      <Container className='text-center mt-5'>
        <h2>{isAr ? 'سلتك فارغة' : 'Your cart is empty'}</h2>
        <Link to='/shopping'>
          <button className='btn-buy mt-3'>{isAr ? 'تسوق الآن' : 'Shop now'}</button>
        </Link>
      </Container>
    );
  }

  const fieldErr = (key) => (errors[key] ? <div style={errStyle}>{errors[key]}</div> : null);

  return (
    <>
      <Meta title={isAr ? 'تمرات - إتمام الطلب' : 'Tamrat Dates - Checkout'} />
      <Container className='mb-5'>
        <h1 className='mt-3 mb-2 text-center'>{isAr ? 'إتمام الطلب' : 'Checkout'}</h1>
        <p className='text-center' style={{ color: '#7C9D64', fontSize: 13.5, marginBottom: 24 }}>
          🚚{' '}
          {isAr
            ? 'توصيل خلال 2-5 أيام عمل · شحن مجاني للطلبات فوق 250 ر.س'
            : 'Delivery in 2-5 business days · Free shipping over 250 SAR'}
        </p>

        <Form noValidate onSubmit={submitHandler}>
          <Row>
            {/* Order summary — first in DOM so it tops the page on mobile; sticky on desktop */}
            <Col xs={12} md={5} className='order-md-2 mb-4'>
              <div
                className='checkout-summary'
                style={{ background: '#fafaf7', border: '1px solid #e5e5df', borderRadius: '12px', padding: '16px' }}
              >
                <button type='button' className='summary-toggle' onClick={() => setSummaryOpen((o) => !o)}>
                  <span style={{ fontWeight: 600, fontSize: 18 }}>
                    {isAr ? 'ملخص الطلب' : 'Order summary'} <span className='summary-chevron'>⌄</span>
                  </span>
                  <span style={{ fontWeight: 700 }}>
                    {country ? cart.totalPrice : cart.itemsPrice} {t('sar')}
                  </span>
                </button>
                <div className={`summary-body ${summaryOpen ? 'open' : ''}`}>
                {cartItems.map((item) => (
                  <div key={item.id || item.slug} className='d-flex align-items-center mb-2' style={{ gap: '10px' }}>
                    <Image src={item.image_path || item.image} alt='' rounded style={{ width: 48, height: 48, objectFit: 'cover' }} />
                    <div style={{ flex: 1, fontSize: 14 }}>
                      {isAr ? item.name_ar || item.name : item.name_en || item.name}
                      <span style={{ color: '#888' }}> × {item.qty}</span>
                    </div>
                    <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                      {(parseFloat(item.price) * item.qty).toFixed(2)} {t('sar')}
                    </div>
                  </div>
                ))}
                <hr />
                <div className='d-flex justify-content-between' style={{ fontSize: 14 }}>
                  <span>{isAr ? 'سعر المنتجات' : 'Items'}</span>
                  <span>{cart.itemsPrice} {t('sar')}</span>
                </div>
                <div className='d-flex justify-content-between' style={{ fontSize: 14 }}>
                  <span>
                    {isAr ? 'الشحن' : 'Shipping'}
                    <span style={{ display: 'block', color: '#888', fontSize: 12 }}>
                      {isAr ? 'توصيل قياسي · 2-5 أيام عمل' : 'Standard · 2-5 business days'}
                    </span>
                  </span>
                  <span>
                    {country ? `${cart.shippingPrice} ${t('sar')}` : isAr ? 'حسب الدولة' : 'set by country'}
                  </span>
                </div>
                {Number(cart.discount) > 0 && (
                  <div className='d-flex justify-content-between' style={{ fontSize: 14 }}>
                    <span>{isAr ? 'الخصم' : 'Discount'}</span>
                    <span>-{cart.discount} {t('sar')}</span>
                  </div>
                )}
                <hr />
                <div className='d-flex justify-content-between' style={{ fontWeight: 700 }}>
                  <span>{isAr ? 'المجموع' : 'Total'}</span>
                  <span>{country ? `${cart.totalPrice} ${t('sar')}` : '—'}</span>
                </div>
                {ksa && Number(cart.itemsPrice) <= 250 && (
                  <div className='mt-2' style={{ fontSize: 13, color: '#7C9D64' }}>
                    {isAr ? 'أضف منتجات بقيمة تتجاوز 250 ر.س للحصول على شحن مجاني' : 'Free shipping on orders above 250 SAR'}
                  </div>
                )}
                <div className='mt-3' style={{ textAlign: 'center' }}>
                  <button
                    type='button'
                    onClick={() => dispatch(openCart())}
                    style={{ background: 'none', border: 'none', color: '#7C9D64', fontSize: 13.5, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    {isAr ? 'تعديل السلة' : 'Edit cart'}
                  </button>
                </div>
                </div>
              </div>
            </Col>

            {/* Contact + delivery + payment form */}
            <Col xs={12} md={7} className='order-md-1'>
              {/* ---- Section 1: Contact ---- */}
              <div style={sectionTitleStyle}>
                <span style={stepBadgeStyle}>1</span>
                {isAr ? 'معلومات التواصل' : 'Contact details'}
              </div>
              <Form.Group className='my-2' data-field='name'>
                <Form.Label>{isAr ? 'الاسم الكامل' : 'Full Name'}</Form.Label>
                <Form.Control
                  type='text'
                  placeholder={isAr ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                  value={fullName}
                  isInvalid={!!errors.name}
                  onChange={(e) => setFullName(e.target.value)}
                />
                {fieldErr('name')}
              </Form.Group>
              <Form.Group className='my-2' data-field='phone'>
                <Form.Label>{isAr ? 'رقم الجوال' : 'Phone Number'}</Form.Label>
                <div dir='ltr'>
                  <PhoneInput
                    country={phoneCountry}
                    value={phone}
                    onChange={(value) => setPhone(value)}
                    enableSearch
                    disableSearchIcon
                    searchPlaceholder={isAr ? 'ابحث عن دولة' : 'Search country'}
                    countryCodeEditable={false}
                    inputStyle={{ width: '100%', height: '42px' }}
                    inputProps={{ name: 'phone' }}
                  />
                </div>
                {fieldErr('phone')}
              </Form.Group>
              <Form.Group className='my-2' data-field='email'>
                <Form.Label>
                  {isAr ? 'البريد الإلكتروني' : 'Email'}{' '}
                  <span style={{ color: '#999', fontSize: 12, fontWeight: 400 }}>
                    {ksa ? (isAr ? '(اختياري)' : '(optional)') : (isAr ? '(مطلوب للطلبات الدولية)' : '(required for international)')}
                  </span>
                </Form.Label>
                <Form.Control
                  type='email'
                  dir='ltr'
                  placeholder='you@example.com'
                  value={email}
                  isInvalid={!!errors.email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {fieldErr('email')}
              </Form.Group>

              {/* ---- Section 2: Delivery address ---- */}
              <div style={sectionTitleStyle}>
                <span style={stepBadgeStyle}>2</span>
                {isAr ? 'عنوان التوصيل' : 'Delivery address'}
              </div>

              <Form.Group className='my-2' data-field='country'>
                <Form.Label>{t('shipping8')}</Form.Label>
                <Form.Control as='select' value={country} isInvalid={!!errors.country} onChange={handleCountryChange}>
                  <option value=''>{t('shipping9')}</option>
                  {error ? (
                    <option disabled>{error?.data?.message || error.error}</option>
                  ) : (
                    countries.map((c) => (
                      <option key={c.id} value={isAr ? c.country_ar : c.country_en}>
                        {isAr ? c.country_ar : c.country_en}
                      </option>
                    ))
                  )}
                </Form.Control>
                {fieldErr('country')}
              </Form.Group>

              {ksa ? (
                <Form.Group className='my-2' data-field='city'>
                  <Form.Label>{t('shipping4')}</Form.Label>
                  <Form.Control as='select' value={city} isInvalid={!!errors.city} onChange={(e) => setCity(e.target.value)}>
                    <option value=''>{t('shipping15')}</option>
                    {countries.find((c) => c.id === 1)?.cities.map((ct) => (
                      <option key={ct.id} value={ct.city_en}>
                        {isAr ? ct.city_ar : ct.city_en}
                      </option>
                    ))}
                  </Form.Control>
                  {fieldErr('city')}
                </Form.Group>
              ) : (
                <Form.Group className='my-2' data-field='city'>
                  <Form.Label>{t('shipping4')}</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder={t('shipping5')}
                    value={city}
                    isInvalid={!!errors.city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                  {fieldErr('city')}
                </Form.Group>
              )}

              <Form.Group className='my-2' data-field='address'>
                <Form.Label>{t('shipping2')}</Form.Label>
                <Form.Control
                  type='text'
                  placeholder={t('shipping3')}
                  value={address}
                  isInvalid={!!errors.address}
                  onChange={(e) => setAddress(e.target.value)}
                />
                {fieldErr('address')}
              </Form.Group>

              {/* Gift option — Bateel-style minimal toggle */}
              <div className='my-3'>
                {!isGift ? (
                  <button
                    type='button'
                    onClick={() => setIsGift(true)}
                    style={{
                      border: '1px solid #cfcfcf',
                      background: '#fff',
                      borderRadius: '8px',
                      padding: '11px 22px',
                      letterSpacing: '0.3px',
                      cursor: 'pointer',
                      color: '#333',
                      fontSize: '14px',
                    }}
                  >
                    🎁 {isAr ? 'أضف رسالة إهداء' : 'Add a gift message'}
                  </button>
                ) : (
                  <div style={{ border: '1px solid #e5e5df', borderRadius: '12px', padding: '16px', background: '#fafaf7' }}>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <strong>{isAr ? 'تفاصيل الهدية' : 'Gift details'}</strong>
                      <span
                        style={{ cursor: 'pointer', color: '#888', fontSize: '20px', lineHeight: 1 }}
                        onClick={() => setIsGift(false)}
                      >
                        ×
                      </span>
                    </div>
                    <Form.Group className='my-2' data-field='recipientName'>
                      <Form.Label>{isAr ? 'اسم المستلم' : 'Recipient name'}</Form.Label>
                      <Form.Control
                        type='text'
                        value={recipientName}
                        isInvalid={!!errors.recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                      />
                      {fieldErr('recipientName')}
                    </Form.Group>
                    <Form.Group className='my-2' data-field='recipientPhone'>
                      <Form.Label>{isAr ? 'جوال المستلم' : 'Recipient phone'}</Form.Label>
                      <div dir='ltr'>
                        <PhoneInput
                          country={phoneCountry}
                          value={recipientPhone}
                          onChange={(value) => setRecipientPhone(value)}
                          enableSearch
                          disableSearchIcon
                          searchPlaceholder={isAr ? 'ابحث عن دولة' : 'Search country'}
                          countryCodeEditable={false}
                          inputStyle={{ width: '100%', height: '42px' }}
                          inputProps={{ name: 'recipientPhone' }}
                        />
                      </div>
                      {fieldErr('recipientPhone')}
                    </Form.Group>
                    <Form.Group className='my-2'>
                      <Form.Label>{isAr ? 'رسالة الهدية (اختياري)' : 'Gift message (optional)'}</Form.Label>
                      <Form.Control as='textarea' value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} />
                    </Form.Group>
                  </div>
                )}
              </div>

              {/* ---- Section 3: Payment ---- */}
              <div style={sectionTitleStyle}>
                <span style={stepBadgeStyle}>3</span>
                {isAr ? 'طريقة الدفع' : 'Payment'}
              </div>
              <div style={{ border: '1px solid #e5e5df', borderRadius: '12px', padding: '16px', background: '#fafaf7' }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>{t('payment3')}</div>
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e5df',
                    borderRadius: '10px',
                    padding: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: '18px',
                  }}
                >
                  <img
                    src={isAr ? payMethodsAr : payMethodsEn}
                    alt='mada · Apple Pay · Mastercard · Visa'
                    style={{ maxWidth: '320px', width: '100%', height: 'auto', objectFit: 'contain', flex: '1 1 240px' }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#4b2e83', whiteSpace: 'nowrap' }}>STC&nbsp;Pay</span>
                </div>
              </div>

              <div className='text-center' style={{ fontSize: 13, color: '#666', marginTop: 16 }}>
                🔒 {isAr ? 'دفع آمن ومشفّر' : 'Secure encrypted payment'}
              </div>

              <button id='check' type='submit' disabled={placing} className='btn btn-buy mx-1 mt-3 mb-2 w-100'>
                {placing ? (isAr ? 'جاري إنشاء الطلب...' : 'Placing order...') : (isAr ? 'المتابعة للدفع' : 'Continue to payment')}
              </button>

              <p style={{ fontSize: 12.5, color: '#888', textAlign: 'center', margin: '6px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {isAr ? 'بتقديم طلبك، فإنك توافق على ' : 'By placing your order, you agree to our '}
                <Link to='/privacy-policy' style={{ display: 'inline' }}>{isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}</Link>
                {isAr ? ' و' : ' and '}
                <Link to='/return-policy' style={{ display: 'inline' }}>{isAr ? 'سياسة الإرجاع' : 'Return Policy'}</Link>
              </p>

              <div className='text-center' style={{ marginTop: 18 }}>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target='_blank'
                  rel='noreferrer'
                  style={{ color: '#25D366', fontWeight: 600, fontSize: 13.5, textDecoration: 'none' }}
                >
                  💬 {isAr ? 'تحتاج مساعدة؟ تواصل معنا عن طريق الواتساب' : 'Need help? Contact us on WhatsApp'}
                </a>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </>
  );
};

export default CheckoutScreen;
